import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { isValidEmail } from '../utils/validation';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const navigate = useNavigate();
    const { error } = useAppSelector((state) => state.auth);
    const isOnline = useNetworkStatus();
    const { showToast } = useToast();
    const { login, isLoading, setError } = useAuth();

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        // Check network status first
        if (!isOnline) {
            setError('No internet connection. Please check your network and try again.');
            return;
        }

        // Validate inputs
        if (!email || !password) {
            setError('Please enter both email and password.');
            return;
        }

        if (!isValidEmail(email)) {
            setError('Please enter a valid email address.');
            return;
        }

        // Call API
        const result = await login({ email, password });

        if (result.success) {
            showToast('Welcome back! Redirecting to dashboard...', 'success');
            navigate('/home');
        } else {
            showToast(result.error || 'Login failed', 'error');
        }
    };

    // Clear error when user starts typing
    const handleEmailChange = (value: string) => {
        if (error) setError(null);
        setEmail(value);
    };

    const handlePasswordChange = (value: string) => {
        if (error) setError(null);
        setPassword(value);
    };

    return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background-secondary)', padding: 'var(--spacing-2xl) var(--spacing-md)' }}>
            <div className="max-w-md w-full" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)' }}>
                <div>
                    <h2 className="text-center font-extrabold" style={{ marginTop: 'var(--spacing-lg)', fontSize: 'var(--font-size-3xl)', color: 'var(--color-text-primary)' }}>
                        Sign in to your account
                    </h2>
                </div>
                <form style={{ marginTop: 'var(--spacing-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }} onSubmit={handleSubmit}>
                    {error && (
                        <div className="flex items-center" style={{ backgroundColor: 'var(--color-danger-100)', border: '1px solid var(--color-danger-400)', color: 'var(--color-danger-700)', padding: 'var(--spacing-md) var(--spacing-md)', borderRadius: 'var(--radius-md)', gap: 'var(--spacing-sm)' }}>
                            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                        <div>
                            <label htmlFor="email" className="block font-medium" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
                                Email address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                className="appearance-none block w-full"
                                style={{ padding: 'var(--spacing-md) var(--spacing-md)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', color: 'var(--color-text-primary)', backgroundColor: 'var(--color-surface)', fontSize: 'var(--font-size-sm)' }}
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => handleEmailChange(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="block font-medium" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                className="appearance-none block w-full"
                                style={{ padding: 'var(--spacing-md) var(--spacing-md)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', color: 'var(--color-text-primary)', backgroundColor: 'var(--color-surface)', fontSize: 'var(--font-size-sm)' }}
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => handlePasswordChange(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div style={{ fontSize: 'var(--font-size-sm)' }}>
                            <Link
                                to="/forgot-password"
                                className="font-medium"
                                style={{ color: 'var(--color-primary-600)' }}
                            >
                                Forgot your password?
                            </Link>
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="group relative w-full flex justify-center font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ padding: 'var(--spacing-md) var(--spacing-md)', border: 'none', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-primary-600)' }}
                        >
                            {isLoading ? 'Signing in...' : 'Sign in'}
                        </button>
                    </div>

                    <div className="text-center">
                        <Link
                            to="/signup"
                            className="font-medium"
                            style={{ color: 'var(--color-primary-600)' }}
                        >
                            Don't have an account? Sign up
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Login;
